/**
 * InsightCardPayloadRenderer
 *
 * Top-level renderer for InsightCardPayload format.
 * Renders each block in a stacked vertical layout.
 * Supports two block formats:
 * - InsightCardBlock: Layout-based with block_elements (renders with InsightCard wrapper)
 * - FreeFlowBlock: Direct UIBlock rendering (renders without wrapper, like chat)
 *
 * Aligns with the API contract from fetchInsightReports.
 */

import React from 'react';
import { Container } from '@strivelabs/ui-blocks';
import type { InsightCardPayload, UIActionEvent } from '@/types/ui-blocks';
import { ReportBlocksRenderer } from './report-blocks-renderer';

export interface InsightCardPayloadRendererProps {
  /** The complete payload with UI blocks */
  payload: InsightCardPayload;
  /** Callback fired when user interacts with UI components */
  onUIAction?: (event: UIActionEvent) => void;
  /** Additional CSS classes for the container */
  className?: string;
}

export const InsightCardPayloadRenderer: React.FC<InsightCardPayloadRendererProps> = ({
  payload,
  onUIAction = () => {},
  className,
}) => {
  const { ui } = payload;

  if (!ui?.blocks || ui.blocks.length === 0) {
    return (
      <Container padding="lg" columns={1} className={className}>
        <div className="p-4 border rounded-lg bg-muted/10">
          <p className="text-sm text-muted-foreground">
            No insight cards available
          </p>
        </div>
      </Container>
    );
  }

  return (
    <Container padding="lg" columns={1} className={className}>
      <ReportBlocksRenderer blocks={ui.blocks} onUIAction={onUIAction} />
    </Container>
  );
};

export default InsightCardPayloadRenderer;
